import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Bookmark } from "lucide-react";
import { ACCENTS } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";

export function FeaturedCard({ c, index = 0 }) {
  const a = ACCENTS[c.accent] || ACCENTS.blue;
  return (
    <Link
      to={`/case/${c.slug}`}
      data-testid={`featured-card-${index}`}
      className="group lift-card block border-2 border-ink overflow-hidden bg-white hover:shadow-[8px_8px_0_0_#0A0A0C]"
    >
      <div className="zoom-frame aspect-[16/10] border-b-2 border-ink">
        <img src={c.image} alt={c.company} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className={`${a.bg} ${a.text} p-5 sm:p-6 flex flex-col min-h-[240px]`}>
        <span className={`self-start eyebrow ${a.badge} px-2 py-1 mb-4`}>{c.industry}</span>
        <h3 className="font-display font-extrabold text-xl sm:text-2xl leading-[1.05] tracking-tight">{c.headline}</h3>
        <p className={`text-sm mt-3 leading-relaxed ${a.sub}`}>{c.summary}</p>
        <div className="mt-auto pt-5 flex items-center justify-between">
          <div>
            <div className="font-bold text-sm">{c.company}</div>
            <div className={`text-xs ${a.sub}`}>{c.country}</div>
          </div>
          <span className={`h-10 w-10 rounded-full border-2 ${c.accent === "lime" || c.accent === "yellow" ? "border-ink" : "border-white"} flex items-center justify-center`}>
            <ArrowRight size={18} className="arrow-move" />
          </span>
        </div>
      </div>
    </Link>
  );
}

const ACCENT_KEYS = ["blue", "orange", "lime", "pink", "yellow"];
function industryAccent(industry) {
  const s = String(industry || "other");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return ACCENT_KEYS[h % ACCENT_KEYS.length];
}

export function CaseCard({ c }) {
  const { isSaved, toggleSaved } = useSite();
  const saved = isSaved(c.slug);
  const acc = industryAccent(c.industry);
  const dot = { blue: "bg-cobalt text-white", orange: "bg-brorange text-white", lime: "bg-brlime text-ink", pink: "bg-brpink text-white", yellow: "bg-bryellow text-ink" }[acc];
  const barBg = { blue: "bg-cobalt", orange: "bg-brorange", lime: "bg-brlime", pink: "bg-brpink", yellow: "bg-bryellow" }[acc];
  const metricText = { blue: "text-cobalt", orange: "text-brorange", lime: "text-ink", pink: "text-brpink", yellow: "text-ink" }[acc];
  const footerTint = { blue: "bg-cobalt/10", orange: "bg-brorange/10", lime: "bg-brlime/25", pink: "bg-brpink/10", yellow: "bg-bryellow/40" }[acc];
  const arrowHover = { blue: "group-hover:bg-cobalt", orange: "group-hover:bg-brorange", lime: "group-hover:bg-brlime", pink: "group-hover:bg-brpink", yellow: "group-hover:bg-bryellow" }[acc];
  const headlineHover = { blue: "group-hover:text-cobalt", orange: "group-hover:text-brorange", lime: "group-hover:text-brorange", pink: "group-hover:text-brpink", yellow: "group-hover:text-brorange" }[acc];
  return (
    <Link
      to={`/case/${c.slug}`}
      data-testid="case-study-card"
      className="group lift-card relative block border-2 border-ink bg-white overflow-hidden hover:shadow-[6px_6px_0_0_#0A0A0C]"
    >
      <div className="zoom-frame aspect-[16/10] border-b-2 border-ink relative">
        <img src={c.image} alt={c.company} className="w-full h-full object-cover" loading="lazy" />
        <span className={`absolute top-3 left-3 eyebrow px-2.5 py-1 font-bold border-2 border-ink ${dot}`}>{c.industry}</span>
        <button
          data-testid="save-case-study-button"
          onClick={(e) => { e.preventDefault(); toggleSaved(c.slug); }}
          aria-label={saved ? "Remove from saved" : "Save case"}
          className={`absolute top-3 right-3 h-9 w-9 flex items-center justify-center border-2 border-ink transition-colors ${saved ? "bg-ink text-bryellow" : "bg-white text-ink hover:bg-bryellow"}`}
        >
          <Bookmark size={16} fill={saved ? "currentColor" : "none"} />
        </button>
      </div>
      <div className={`h-2 ${barBg} border-b-2 border-ink`} />
      <div className="p-4 flex flex-col min-h-[180px]">
        <h3 className={`font-display font-bold text-lg leading-[1.1] tracking-tight transition-colors ${headlineHover}`}>{c.headline}</h3>
        <p className="text-sm text-ink/60 mt-2 leading-snug line-clamp-2">{c.summary}</p>
        {c.key_metric && (
          <div className="mt-3 flex items-baseline gap-2">
            <span className={`font-display font-extrabold text-2xl tracking-tight ${metricText}`}>{c.key_metric}</span>
            <span className="text-[0.7rem] text-ink/50 leading-tight">{c.metric_context}</span>
          </div>
        )}
        <div className={`mt-auto -mx-4 -mb-4 mt-3 px-4 py-3 flex items-center justify-between border-t-2 border-ink ${footerTint}`}>
          <div className="text-xs text-ink/70">
            <span className="font-bold text-ink">{c.company}</span> · {c.country}
          </div>
          <span className={`h-8 w-8 rounded-full border-2 border-ink flex items-center justify-center bg-white transition-colors ${arrowHover} group-hover:text-white`}>
            <ArrowUpRight size={15} className="arrow-move" />
          </span>
        </div>
      </div>
    </Link>
  );
}
